import { QueryClient, QueryClientProvider, useQuery } from "@tanstack/react-query";
import {
  Navigate,
  Outlet,
  Link,
  createRootRouteWithContext,
  useRouter,
  useRouterState,
} from "@tanstack/react-router";
import type { ReactNode } from "react";

import { auth as authApi, onboarding as onboardingApi } from "../lib/api";
import { AuthProvider, AuthTokenSync } from "../lib/auth";
import { WorkspaceProvider } from "../store/workspaces";

function NotFoundComponent() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-7xl font-bold text-foreground">404</h1>
        <h2 className="mt-4 text-xl font-semibold text-foreground">Page not found</h2>
        <p className="mt-2 text-sm text-muted-foreground">
          The page you're looking for doesn't exist or has been moved.
        </p>
        <div className="mt-6">
          <Link
            to="/hq"
            className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
          >
            Go home
          </Link>
        </div>
      </div>
    </div>
  );
}

function ErrorComponent({ error, reset }: { error: Error; reset: () => void }) {
  console.error(error);
  const router = useRouter();
  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-xl font-semibold tracking-tight text-foreground">This page didn't load</h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Something went wrong on our end. Try refreshing or head back home.
        </p>
        <div className="mt-6 flex flex-wrap justify-center gap-2">
          <button
            onClick={() => {
              router.invalidate();
              reset();
            }}
            className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
          >
            Try again
          </button>
          <a
            href="/"
            className="inline-flex items-center justify-center rounded-md border border-input bg-background px-4 py-2 text-sm font-medium text-foreground transition-colors hover:bg-accent"
          >
            Go home
          </a>
        </div>
      </div>
    </div>
  );
}

function RootComponent() {
  const { queryClient } = Route.useRouteContext();
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <AuthTokenSync />
        <RouteGuard>
          <WorkspaceProvider>
            <Outlet />
          </WorkspaceProvider>
        </RouteGuard>
      </AuthProvider>
    </QueryClientProvider>
  );
}

// Anything outside this list requires a valid /auth/me. /onboarding and
// /onboarding-pending stay "public" so the RouteGuard can park signed-in
// users there without bouncing back to /.
const PUBLIC_PATHS = new Set<string>([
  "/",
  "/onboarding",
  "/onboarding-pending",
]);
const PUBLIC_PREFIXES = ["/auth/"];

function isPublicPath(pathname: string): boolean {
  if (PUBLIC_PATHS.has(pathname)) return true;
  return PUBLIC_PREFIXES.some((p) => pathname.startsWith(p));
}

function RouteGuard({ children }: { children: ReactNode }) {
  const pathname = useRouterState({ select: (s) => s.location.pathname });

  const meQuery = useQuery({
    queryKey: ["me"],
    queryFn: () => authApi.me(),
    retry: false,
    refetchOnWindowFocus: false,
    // Cache for the session — login/logout invalidates this key explicitly.
    staleTime: Infinity,
  });

  const authed = meQuery.isSuccess;

  // Only fetch onboarding state when the user is authenticated. Otherwise we
  // waste a request on every public page paint.
  const onboardingQuery = useQuery({
    queryKey: ["onboarding-status"],
    queryFn: () => onboardingApi.status(),
    enabled: authed,
    retry: false,
    refetchOnWindowFocus: false,
    staleTime: 1000 * 60 * 5,
  });

  if (meQuery.isPending || (authed && onboardingQuery.isPending)) {
    // Avoid flicker — show a minimal full-screen placeholder while we resolve.
    return (
      <div className="grid min-h-screen place-items-center bg-background">
        <div className="text-[12px] text-muted-foreground">Loading…</div>
      </div>
    );
  }

  const onPublic = isPublicPath(pathname);

  // Not signed in and trying to reach a protected page → bounce to login.
  if (!authed && !onPublic) {
    return <Navigate to="/" />;
  }

  // Authenticated. Gate everything on onboarding being completed, with a
  // path that lets the eligible roles actually finish it.
  if (authed) {
    const status = onboardingQuery.data;
    const needsOnboarding = status && !status.completed;
    if (needsOnboarding) {
      // Eligible role (owner/admin/supervisor) → push them to the wizard.
      if (status.can_complete && pathname !== "/onboarding") {
        return <Navigate to="/onboarding" />;
      }
      // Non-eligible role (analyst/viewer/operator/publisher) → polite block.
      if (!status.can_complete && pathname !== "/onboarding-pending") {
        return <Navigate to="/onboarding-pending" />;
      }
    } else {
      // Onboarding done. Bounce out of /onboarding so a finished admin doesn't
      // get stuck there, and out of "/" so they land on /hq.
      if (pathname === "/") {
        return <Navigate to="/hq" />;
      }
      if (pathname === "/onboarding" || pathname === "/onboarding-pending") {
        return <Navigate to="/hq" />;
      }
    }
  }

  return <>{children}</>;
}

export const Route = createRootRouteWithContext<{ queryClient: QueryClient }>()({
  component: RootComponent,
  notFoundComponent: NotFoundComponent,
  errorComponent: ErrorComponent,
});
